import { useState } from "react";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { Loader2, X } from "lucide-react";

export default function AddFreelancerDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const reset = () => {
    setName("");
    setEmail("");
    setPassword("");
    setError("");
    setShowPassword(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) return setError("Name is required.");
    if (!email.trim()) return setError("Email is required.");
    if (password.length < 10)
      return setError("Password must be at least 10 characters.");

    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/users", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        role: "freelancer",
      });
      setSubmitting(false);
      reset();
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Could not create freelancer account."
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      onClick={handleClose}
      data-testid="add-freelancer-dialog"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-6 py-4 text-white rounded-t-2xl flex items-center justify-between"
          style={{
            background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
          }}
        >
          <h3 className="text-base font-semibold">Add freelancer</h3>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white"
            disabled={submitting}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="add-freelancer-form">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Full name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              data-testid="add-name-input"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": BRAND.teal }}
              disabled={submitting}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@wellformmd.com"
              data-testid="add-email-input"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": BRAND.teal }}
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Temporary password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 10 characters"
                data-testid="add-password-input"
                className="w-full px-3 py-2 pr-16 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2"
                style={{ "--tw-ring-color": BRAND.teal }}
                disabled={submitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1"
                tabIndex={-1}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Share this with the freelancer so they can sign in.
            </p>
          </div>
          {error && (
            <div
              className="text-sm px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700"
              data-testid="add-error"
            >
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="add-submit-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold transition disabled:opacity-70"
              style={{
                background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
              }}
            >
              {submitting && <Loader2 className="animate-spin" size={14} />}
              Create account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
